import { C1_CONTRACT } from './possessor-compile';
import { createInventoryAbilityGrantCompiler } from './inventory-ability-grants';
import { resolvePilotedObjectId, pilotedObjectRow, applyPilotedSpeeds, PilotedObjectUnavailable, pilotedStatusPeers } from './piloted-object';
import { createAbilityConditionEvaluator } from './ability-conditions';
import { createFormulaReadContext } from './formula-read-context';
import { statusProjectionDependencies } from './status-attack-budget';
import { readItemField } from './item-field-reader';
import { scoreCarrierIntegerLayer } from './formula-variable-resolution';
import { fromBaseRecord } from './from-base-context';
import { storedStatusRows } from './creature-status-reader';
import { countStatusCompileWork } from './member-status-diff';
import { STATUS_IDS, statusItemId } from '../statuses/expiry';
import { compileCreatureSpeeds } from './creature-speeds';

type Row = Record<string, unknown>;
const upper = (value: unknown): string => String(value ?? '').toUpperCase();
export const isPilotingStatus = (id: string): boolean => id === STATUS_IDS.piloting || id === STATUS_IDS.piloted;

/** A relationship delta replaces only external grants and the seven speed slots.
 * Other abilities, inventory, defenses, skills and encounter members are borrowed. */
export function projectPilotedAbilityDiff(row: Row, after: { target: Row[]; caster: Row[] },
    rows: readonly Row[], peers: readonly Row[], definition: (id: string) => Row | null): string | null {
    const beforePeers = pilotedStatusPeers(rows);
    const priorVehicle = resolvePilotedObjectId(row, beforePeers);
    const wasRestricted = storedStatusRows(row, 'target').some(status => statusItemId(status) === STATUS_IDS.piloted);
    row.statuses = after;
    const vehicleId = resolvePilotedObjectId(row, peers);
    const computed = fromBaseRecord(row.computed) ? row.computed : null;
    const creature = computed && fromBaseRecord(computed.creature) ? computed.creature : null;
    if (!creature) return 'piloted-abilities:pilot-clusters-not-held';
    try {
        const deps = statusProjectionDependencies(row, definition), readers = createFormulaReadContext(deps);
        const conditions = createAbilityConditionEvaluator({ ...deps, equipmentSlots: C1_CONTRACT.equipmentSlots,
            vitalityStatusIds: C1_CONTRACT.vitalityStatusIds, skillEntries: C1_CONTRACT.skillEntries });
        const query = (id: string) => pilotedObjectRow(id, rows, definition);
        const scratch: Row = { ...row };
        for (const key of C1_CONTRACT.grants.inventoryAbilityTypes) scratch[key] = [];
        const projected = createInventoryAbilityGrantCompiler({ ...deps, metadata: C1_CONTRACT.grants,
            queryFullItem: definition, conditions: (ability, host, template, defer) => conditions.check(ability, host, template, defer),
            redundant: () => false, resolvePilotedObjectId: () => vehicleId, queryPilotedObject: query }).piloted(scratch, false) as Row;
        const abilities = fromBaseRecord(creature.abilities) ? creature.abilities : {};
        const changes = new Map<string, unknown[]>();
        for (const key of C1_CONTRACT.grants.inventoryAbilityTypes) {
            const old = Array.isArray(abilities[key]) ? abilities[key] as unknown[] : [];
            const kept = priorVehicle ? old.filter(entry => !fromBaseRecord(entry) || upper(readItemField(entry, 'source')) !== priorVehicle) : old;
            const grants = Array.isArray(projected[key]) ? projected[key] as Row[] : [];
            if (kept.length === old.length && !grants.length) continue;
            const next = [...kept, ...grants.map(entry => ({ item_id: readItemField(entry, 'ability_id'),
                source: readItemField(entry, 'source'), ...(readItemField(entry, 'requires_level') !== 1 ? { requires_level: readItemField(entry, 'requires_level') } : {}) }))];
            changes.set(key, next);
        }
        let speeds = { ...fromBaseRecord(creature.speeds) ? creature.speeds : {} };
        const restricted = storedStatusRows(row, 'target').some(status => statusItemId(status) === STATUS_IDS.piloted);
        if ((priorVehicle || wasRestricted) && !vehicleId && !restricted) {
            const natural = compileCreatureSpeeds(row, deps);
            if (!natural.ok) throw new PilotedObjectUnavailable(`piloted-abilities:dismount-speeds:${natural.reason}`);
            speeds = natural.speeds;
        }
        if (vehicleId) applyPilotedSpeeds(speeds, query(vehicleId), (host, key) => scoreCarrierIntegerLayer(host, key, 0, false, deps.metadata, readers));
        if (restricted && !wasRestricted) applyPilotedSpeeds(speeds, { items: {}, item_layer_types: {} }, () => 0);
        for (const [key, next] of changes) { if (next.length) abilities[key] = next; else delete abilities[key]; }
        creature.abilities = abilities;
        creature.speeds = speeds;
        countStatusCompileWork('piloted', row);
        if (projected._piloted_ability_refusal) row._piloted_ability_refusal = projected._piloted_ability_refusal;
        else delete row._piloted_ability_refusal;
        return null;
    } catch (error) {
        // Retiring the relationship always retires its grants, even if a source/scene
        // dependency is unavailable. Preserve the pilot's other ability entries.
        const abilities = fromBaseRecord(creature.abilities) ? creature.abilities : {};
        if (priorVehicle) for (const key of C1_CONTRACT.grants.inventoryAbilityTypes) {
            const old = abilities[key];
            if (Array.isArray(old)) abilities[key] = old.filter(entry => !fromBaseRecord(entry) || upper(readItemField(entry, 'source')) !== priorVehicle);
        }
        return error instanceof PilotedObjectUnavailable ? error.message : `piloted-abilities:projection:${error instanceof Error ? error.message : String(error)}`;
    }
}
